import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { callApi, HttpMethod } from "src/services/apiService";
import { toast } from "sonner";
import { TC } from "../utils/trustCallApi";
import type { LegalEntitySelectItem, LegalEntity } from "../types";

export function useLegalEntities() {
  const queryClient = useQueryClient();

  const listQuery = useQuery<LegalEntitySelectItem[]>({
    queryKey: ["trustcall", "settings", "legal-entities"],
    queryFn: () => callApi(TC.SETTINGS_LEGAL_ENTITIES, HttpMethod.GET),
  });

  const getByName = (legalName: string) =>
    callApi(`${TC.SETTINGS_LEGAL_ENTITIES}/${encodeURIComponent(legalName)}`, HttpMethod.GET) as Promise<LegalEntity>;

  const upsert = useMutation({
    mutationFn: (data: Partial<LegalEntity>) =>
      callApi(TC.SETTINGS_LEGAL_ENTITIES, HttpMethod.POST, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trustcall", "settings"] });
      toast.success("Legal entity saved");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to save legal entity"),
  });

  const rename = useMutation({
    mutationFn: (data: { origLegalCompanyName: string; newLegalCompanyName: string }) =>
      callApi(TC.SETTINGS_LEGAL_COMPANY_NAME, HttpMethod.PUT, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trustcall", "settings"] });
      toast.success("Legal company name updated");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to rename legal company"),
  });

  return {
    legalEntities: listQuery.data,
    isLoading: listQuery.isLoading,
    getByName,
    upsert,
    rename,
  };
}
